import React, { useState, useCallback } from 'react';
import { Typography, Avatar, IconButton, Menu, MenuItem } from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  Insights,
  Settings,
  TrackChanges,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { performLogout } from '../services/sessionExpiry';

/**
 * The avatar button and the menu behind it, shared by every app bar.
 *
 * This existed in triplicate — UserAppBar, StaffAppBar, and SystemAdmin's own
 * inline bar each wrote it out by hand — and the three copies had drifted into
 * three different menus: SystemAdmin's offered no way to reach My Account,
 * UserAppBar's offered nothing but, and the two dashboard entries appeared in
 * opposite orders with no icons. One copy is what keeps them from drifting
 * again, so ITEMS below is the only place the order is written down.
 *
 * The bars themselves stay separate components. What distinguishes them is
 * branding for two different audiences — title, ADMIN chip — and that is worth
 * keeping apart; the menu is the part that should be identical everywhere.
 *
 * Self-contained rather than prop-driven, matching the bars that host it: the
 * items and their targets are the same on every page, so there is nothing for a
 * caller to parameterise.
 */

const ADMIN_PERMISSION = 'can_view_admin_dash';
const GOALS_PERMISSION = 'can_view_goals';

// Order is the contract: My Account first, admin before user dashboard, System
// Admin last before Logout. Logout is rendered separately below because it
// navigates nowhere.
const ITEMS = [
  { label: 'My Account', route: '/account', Icon: AccountCircle },
  {
    label: 'Admin Dashboard',
    route: '/admin/dashboard',
    Icon: Insights,
    permission: ADMIN_PERMISSION,
  },
  {
    label: 'User Dashboard',
    route: '/dashboard',
    Icon: TrackChanges,
    permission: GOALS_PERMISSION,
  },
  {
    label: 'System Admin',
    route: '/system-admin',
    Icon: Settings,
    permission: ADMIN_PERMISSION,
  },
];

/**
 * The permissions postLogin stored, or none.
 *
 * Anything other than a JSON array means the key has been cleared halfway or
 * edited by hand. Treating that as "no permissions" hides the optional items;
 * letting it throw would take the whole app bar — including Logout — down with
 * it, stranding whoever hit it on a page they cannot leave.
 */
const readPermissions = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('userPermissions') || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

function AccountMenu() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const username = localStorage.getItem('username');
  const permissions = readPermissions();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Closing before navigating matters where the bar survives the route change:
  // otherwise the menu is still sitting open over the page that was navigated
  // to. The three hand-written copies all left it open.
  const handleSelect = (route) => {
    setAnchorEl(null);
    navigate(route);
  };

  // performLogout clears all five session keys. SystemAdmin's hand-rolled
  // version listed them itself, which is how UserAppBar's came to be missing
  // userPermissions before it was replaced with this call.
  const handleLogout = useCallback(() => {
    setAnchorEl(null);
    performLogout(navigate);
  }, [navigate]);

  const visible = ITEMS.filter(
    ({ permission }) => !permission || permissions.includes(permission),
  );

  return (
    <div>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
      >
        <Avatar sx={{ bgcolor: 'secondary.main' }}>
          {username ? username[0].toUpperCase() : <AccountCircle />}
        </Avatar>
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem disabled>
          <Typography variant="body2">{username}</Typography>
        </MenuItem>
        {visible.map(({ label, route, Icon }) => (
          <MenuItem key={route} onClick={() => handleSelect(route)}>
            <Icon sx={{ mr: 1 }} /> {label}
          </MenuItem>
        ))}
        <MenuItem onClick={handleLogout}>
          <ExitToApp sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>
    </div>
  );
}

export default AccountMenu;
